import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Target, ListChecks, FileSpreadsheet, Mail, Settings, Sparkles, History, Search, Plus, MoreVertical, Shield, CheckCircle2, XCircle, ExternalLink, Save, ToggleLeft, ToggleRight } from 'lucide-react';
type AdminTab = 'users' | 'competencies' | 'questions' | 'templates' | 'emails' | 'platform' | 'ai' | 'audit';
export function Administration() {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const tabs = [{
    id: 'users',
    label: 'User Management',
    icon: Users
  }, {
    id: 'competencies',
    label: 'Competency Management',
    icon: Target
  }, {
    id: 'questions',
    label: 'Question Management',
    icon: ListChecks
  }, {
    id: 'templates',
    label: 'Assessment Templates',
    icon: FileSpreadsheet
  }, {
    id: 'emails',
    label: 'Email Templates',
    icon: Mail
  }, {
    id: 'platform',
    label: 'Platform Settings',
    icon: Settings
  }, {
    id: 'ai',
    label: 'AI Settings',
    icon: Sparkles
  }, {
    id: 'audit',
    label: 'Audit Logs',
    icon: History
  }] as const;
  return <div className="space-y-6 animate-in fade-in duration-500 pb-12" data-id="element-90" data-mp="1vs4p2wwi5len">
      {/* Header */}
      <div data-id="element-91" data-mp="2jhyyt5liytuo">
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center" data-id="element-92" data-mp="2jhfi1s7m2bi8">
          <Shield className="h-6 w-6 mr-3 text-slate-700" data-id="element-93" data-mp="2oh26kxsw3e6c" />
          Administration
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-94" data-mp="29s5qcdgoiuun">
          Manage system configurations, users, and platform-wide settings.
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-id="element-95" data-mp="2eq79eye3uu64">
        {/* Left Sidebar Nav */}
        <div className="w-full md:w-64 shrink-0" data-id="element-96" data-mp="32gl0omov8ozn">
          <nav className="space-y-1" data-id="element-97" data-mp="32eqjnjgpth0u">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} data-id="element-98" data-mp="3h2qrlyfcxq4o">
                  <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-99" data-mp="2y57i6tozm5hq" />
                  {tab.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-100" data-mp="2kdj6usmkbl1t">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[500px]" data-id="element-101" data-mp="2kekhivzo25j0">
            {activeTab === 'users' && <UserManagementPanel data-id="element-102" data-mp="2fg0wcxswcxrp" />}
            {activeTab === 'competencies' && <CompetencyManagementPanel data-id="element-103" data-mp="2aflkybs6e0cd" />}
            {activeTab === 'questions' && <QuestionManagementPanel data-id="element-104" data-mp="pxu790f5967t" />}
            {activeTab === 'templates' && <AssessmentTemplatesPanel data-id="element-105" data-mp="uw5gpa1j1epi" />}
            {activeTab === 'emails' && <EmailTemplatesPanel data-id="element-106" data-mp="uuaxq19tfwo2" />}
            {activeTab === 'platform' && <PlatformSettingsPanel data-id="element-107" data-mp="zw9zj6pwqliw" />}
            {activeTab === 'ai' && <AISettingsPanel data-id="element-108" data-mp="13wtv45uu1h2x" />}
            {activeTab === 'audit' && <AuditLogsPanel data-id="element-109" data-mp="13wadyrd0awpy" />}
          </div>
        </div>
      </div>
    </div>;
}
// --- Individual Panels ---
function UserManagementPanel() {
  return <div className="flex flex-col h-full" data-id="element-110" data-mp="c54kgtrsjx9n">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-111" data-mp="c65ucux2mdps">
        <div data-id="element-112" data-mp="g5oevpkzcms3">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-113" data-mp="g550giujggit">
            User Management
          </h2>
          <p className="text-sm text-slate-500" data-id="element-114" data-mp="kzsqwn93rl01">
            Manage administrators, managers, and system access.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-115" data-mp="3om9h8nj3whm8">
          <Plus className="h-4 w-4 mr-2" data-id="element-116" data-mp="35gbsavqmzz38" />
          Invite User
        </button>
      </div>
      <div className="p-4 border-b border-slate-200" data-id="element-117" data-mp="30l641q9ri8bz">
        <div className="relative max-w-md" data-id="element-118" data-mp="3f8hfzt9uvucm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-119" data-mp="3fac41ggfc2kx" />
          <input type="text" placeholder="Search users by name or email..." className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:bg-white focus:border-slate-300 outline-none" data-id="element-120" data-mp="3afy4d1uqb9ag" />
        </div>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-121" data-mp="20o31vyny83q">
        <table className="w-full text-sm text-left" data-id="element-122" data-mp="1zd2e50ilggu">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-123" data-mp="3t9bsan00yfgk">
            <tr data-id="element-124" data-mp="23tvlhe9kp5tu">
              <th className="px-6 py-3 font-medium" data-id="element-125" data-mp="23tcegslgo1ul">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-126" data-mp="1yurd2puh3c4k">Role</th>
              <th className="px-6 py-3 font-medium" data-id="element-127" data-mp="1ywc78ecvkt1w">Status</th>
              <th className="px-6 py-3 font-medium text-right" data-id="element-128" data-mp="ecgtwx8z9m2v">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-129" data-mp="ja8k9mtz504y">
            {[{
            name: 'Jane Doe',
            email: 'jane@example.com',
            role: 'System Admin',
            status: 'Active'
          }, {
            name: 'Michael Scott',
            email: 'michael@example.com',
            role: 'Manager',
            status: 'Active'
          }, {
            name: 'Sarah Connor',
            email: 'sarah@example.com',
            role: 'Manager',
            status: 'Pending'
          }].map((user, i) => <tr key={i} className="hover:bg-slate-50" data-id="element-130" data-mp="1g0slin185csw" data-mpi={i}>
                <td className="px-6 py-4" data-id="element-131" data-mp="1fz7rqzmqhxw3">
                  <div className="font-medium text-slate-900" data-id="element-132" data-mp="1l0neqx8h2eh4">{user.name}</div>
                  <div className="text-slate-500" data-id="element-133" data-mp="1jiyjq72mo3n2">{user.email}</div>
                </td>
                <td className="px-6 py-4" data-id="element-134" data-mp="1ek54a93s62k9">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-135" data-mp="1ej3ppunk2xxs">
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-136" data-mp="14niklnn6xhjt">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === 'Active' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-137" data-mp="229py5n1h2k8b">
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-138" data-mp="1nllg8la8ucql">
                  <button className="text-slate-400 hover:text-slate-600" data-id="element-139" data-mp="1niqrwio407rw">
                    <MoreVertical className="h-4 w-4" data-id="element-140" data-mp="2l1s70c7vgw80" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function CompetencyManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-141" data-mp="2sjqc84j0tl5f">
      <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-142" data-mp="2sk07a6ixaihz">
        <Target className="h-8 w-8 text-indigo-600" data-id="element-143" data-mp="2xhi97cyivxgh" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-144" data-mp="32ixqctxkxjtl">
        Competency Library
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-145" data-mp="37fxv91ip0gj4">
        Competencies define the core skills and behaviors measured in
        assessments. They are managed in a dedicated library to support
        categorization, tagging, and detailed descriptions — and each
        competency is mapped to the leadership levels it applies to, from
        Individual Contributor through Executive.
      </p>
      <Link to="/app/competencies" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-146" data-mp="3q8a0bw0qdvwy">
        Open Competency Library
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-147" data-mp="kakylegw7366" />
      </Link>
    </div>;
}
function QuestionManagementPanel() {
  return <div className="p-8 text-center max-w-lg mx-auto mt-12" data-id="element-148" data-mp="ffdwgb39u6jd">
      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-149" data-mp="tbj4y3eszmrx">
        <ListChecks className="h-8 w-8 text-blue-600" data-id="element-150" data-mp="oe94zttsfspu" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2" data-id="element-151" data-mp="ndgp6ojjfnc">
        Question Bank
      </h2>
      <p className="text-slate-500 mb-8" data-id="element-152" data-mp="3ssha6rr8gxyt">
        Questions are managed centrally to ensure consistency across
        assessments. You can create templates, categorize by rating type, and
        bulk import questions in the dedicated library.
      </p>
      <Link to="/app/questions" className="inline-flex items-center bg-slate-900 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-153" data-mp="5l59e2x0f5sl">
        Open Question Library
        <ExternalLink className="h-4 w-4 ml-2" data-id="element-154" data-mp="1om65hz9q5b0d" />
      </Link>
    </div>;
}
function AssessmentTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-155" data-mp="1tcwcfgxstlx6">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-156" data-mp="1tequmnhoqzxk">
        <div data-id="element-157" data-mp="1fj5060sonnxr">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-158" data-mp="1kkc5zub9md1s">
            Assessment Templates
          </h2>
          <p className="text-sm text-slate-500" data-id="element-159" data-mp="28a6jme3es5n3">
            Standardized frameworks for quick assessment creation.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-160" data-mp="2ca74ohl0jf34">
          <Plus className="h-4 w-4 mr-2" data-id="element-161" data-mp="23atz4000fano" />
          New Template
        </button>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-162" data-mp="mwk78vwk73s3">
        {[{
        name: 'Standard 360° Executive Review',
        type: '360 Review',
        questions: 45
      }, {
        name: 'Quarterly Manager Check-in',
        type: 'Self/Manager',
        questions: 15
      }, {
        name: 'Peer Feedback Pulse',
        type: 'Peer Only',
        questions: 8
      }].map((template, i) => <div key={i} className="border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors cursor-pointer group" data-id="element-163" data-mp="11avb29me66em" data-mpi={i}>
            <div className="flex justify-between items-start mb-3" data-id="element-164" data-mp="ndgiwx4r7kiy">
              <div className="p-2 bg-slate-100 rounded-lg text-slate-600 group-hover:bg-slate-900 group-hover:text-white transition-colors" data-id="element-165" data-mp="rdqklxlijos0">
                <FileSpreadsheet className="h-5 w-5" data-id="element-166" data-mp="dfiuo9mw9nqc" />
              </div>
              <button className="text-slate-400 hover:text-slate-600" data-id="element-167" data-mp="icj1imrkiuii">
                <MoreVertical className="h-4 w-4" data-id="element-168" data-mp="27s8qiwwtp70d" />
              </button>
            </div>
            <h3 className="font-semibold text-slate-900 mb-1" data-id="element-169" data-mp="2cqjzl5fandhj">
              {template.name}
            </h3>
            <div className="flex items-center text-xs text-slate-500 space-x-3" data-id="element-170" data-mp="13zvr1h6qqzj">
              <span data-id="element-171" data-mp="3sa11cuidssds">{template.type}</span>
              <span data-id="element-172" data-mp="3sb2ohxco95bv">•</span>
              <span data-id="element-173" data-mp="asrkl0qy8v68">{template.questions} Questions</span>
            </div>
          </div>)}
      </div>
    </div>;
}
function EmailTemplatesPanel() {
  return <div className="flex flex-col h-full" data-id="element-174" data-mp="60qclr0tdqm0">
      <div className="p-5 border-b border-slate-200 bg-slate-50" data-id="element-175" data-mp="jt6c4huib6i6">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-176" data-mp="jvizxbmy4b35">
          Email Templates
        </h2>
        <p className="text-sm text-slate-500" data-id="element-177" data-mp="ewfx7xufugyc">
          Customize automated system communications.
        </p>
      </div>
      <div className="flex-1 flex" data-id="element-178" data-mp="ossn0kmv4ant">
        <div className="w-1/3 border-r border-slate-200 overflow-y-auto" data-id="element-179" data-mp="otuajol2epmf">
          <div className="divide-y divide-slate-100" data-id="element-180" data-mp="2vih07fzh8kzz">
            {['Assessment Invitation', 'Reminder: 3 Days Left', 'Reminder: 24 Hours', 'Assessment Completed', 'Report Ready'].map((tpl, i) => <button key={i} className={`w-full text-left px-4 py-3 text-sm transition-colors ${i === 0 ? 'bg-indigo-50 text-indigo-700 font-medium border-l-2 border-indigo-600' : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-181" data-mp="3k9d6amwz48z3" data-mpi={i}>
                {tpl}
              </button>)}
          </div>
        </div>
        <div className="flex-1 p-6 bg-slate-50/50" data-id="element-182" data-mp="2zostnbocddh">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-id="element-183" data-mp="71s8ko5dm44k">
            <div className="p-4 border-b border-slate-200" data-id="element-184" data-mp="btjqkaagv1j3">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-185" data-mp="bw5zoed09p3f">
                Subject Line
              </label>
              <input type="text" defaultValue="Action Required: 360° Leadership Assessment for {{target_name}}" className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-186" data-mp="lssg8qyattzn" />
            </div>
            <div className="p-4" data-id="element-187" data-mp="26909dzin5qwm">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-188" data-mp="26c4mxnhzxyzy">
                Email Body
              </label>
              <textarea rows={8} defaultValue={`Hi {{respondent_name}},\n\nYou have been invited to participate in a 360° Leadership Assessment for {{target_name}}.\n\nYour feedback is crucial for their professional development. Please complete the assessment by {{due_date}}.\n\nClick the link below to begin:\n{{assessment_link}}\n\nThank you,\nThe HR Team`} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm font-mono text-slate-700 focus:ring-2 focus:ring-indigo-100 outline-none" data-id="element-189" data-mp="1wc3zpqh0ayqh" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end" data-id="element-190" data-mp="3cryga3k3buc0">
              <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center" data-id="element-191" data-mp="37xavofs7jk2p">
                <Save className="h-4 w-4 mr-2" data-id="element-192" data-mp="2xhia56zt463n" />
                Save Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function PlatformSettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-193" data-mp="2nit3699g8626">
      <h2 className="text-lg font-semibold text-slate-900 mb-6" data-id="element-194" data-mp="3b7vjndscjsv3">
        Platform Settings
      </h2>

      <div className="space-y-8" data-id="element-195" data-mp="3g6ynww0i5v20">
        <section data-id="element-196" data-mp="3g5x15ua4jk4g">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-197" data-mp="3g6f7xkuexgqm">
            Branding & Appearance
          </h3>
          <div className="space-y-4" data-id="element-198" data-mp="36gvoacyvliup">
            <div data-id="element-199" data-mp="36fucg6a1csbx">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-200" data-mp="36fc78k5eb3rv">
                Organization Name
              </label>
              <input type="text" defaultValue="Acme Corp" className="w-full max-w-md px-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-201" data-mp="2s2697tbaxi9" />
            </div>
            <div data-id="element-202" data-mp="1ij4e1ytx2s45">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-203" data-mp="1ihjqwu9xmbgb">
                Primary Color (Hex)
              </label>
              <div className="flex items-center space-x-3" data-id="element-204" data-mp="1s8xo2veeuz6a">
                <input type="text" defaultValue="#0f172a" className="w-32 px-3 py-2 border border-slate-200 rounded-md text-sm font-mono focus:ring-2 focus:ring-slate-100 outline-none" data-id="element-205" data-mp="1sbjvmv1axeoi" />
                <div className="w-8 h-8 rounded-md bg-slate-900 border border-slate-200" data-id="element-206" data-mp="1dl2s9653weoz"></div>
              </div>
            </div>
          </div>
        </section>

        <section data-id="element-207" data-mp="2oyx6rio9ze2l">
          <h3 className="text-sm font-medium text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-208" data-mp="2oyf1jwjmxpij">
            Assessment Defaults
          </h3>
          <div className="space-y-4" data-id="element-209" data-mp="2kzxs36cqs2xy">
            <div className="flex items-center justify-between max-w-md" data-id="element-210" data-mp="1hnh05fhd1nzw">
              <div data-id="element-211" data-mp="1mm20dewm47oc">
                <div className="text-sm font-medium text-slate-700" data-id="element-212" data-mp="1mlisys4l91ok">
                  Require Minimum Respondents
                </div>
                <div className="text-xs text-slate-500" data-id="element-213" data-mp="1rln0hsniqm2n">
                  Prevent report generation if under threshold
                </div>
              </div>
              <select className="px-3 py-1.5 border border-slate-200 rounded-md text-sm outline-none" data-id="element-214" data-mp="ywzlgetd4sxj">
                <option data-id="element-215" data-mp="13v110prkya49">3 Respondents</option>
                <option data-id="element-216" data-mp="13vsn99zaevhv">5 Respondents</option>
                <option data-id="element-217" data-mp="17wmd48waikde">No Minimum</option>
              </select>
            </div>
            <div className="flex items-center justify-between max-w-md" data-id="element-218" data-mp="1cnunb75g29rm">
              <div data-id="element-219" data-mp="1cpz06elbmj41">
                <div className="text-sm font-medium text-slate-700" data-id="element-220" data-mp="g5ydj1a9ln1u">
                  Enforce Anonymity
                </div>
                <div className="text-xs text-slate-500" data-id="element-221" data-mp="k6s0nsfvuxto">
                  Hide individual rater names on reports
                </div>
              </div>
              <ToggleRight className="h-6 w-6 text-emerald-500" data-id="element-222" data-mp="cly6306ycsjf" />
            </div>
          </div>
        </section>

        <button className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-223" data-mp="3nchooesujaq">
          Save Changes
        </button>
      </div>
    </div>;
}
function AISettingsPanel() {
  return <div className="p-6 max-w-2xl" data-id="element-224" data-mp="3pvm5ig92eeyi">
      <div className="flex items-center mb-6" data-id="element-225" data-mp="3pvvohjvozlup">
        <Sparkles className="h-5 w-5 text-indigo-600 mr-2" data-id="element-226" data-mp="3l3cawmx08qpw" />
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-227" data-mp="3l214efc6d4v3">
          AI Features Configuration
        </h2>
      </div>

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4 mb-8 text-sm text-indigo-800" data-id="element-228" data-mp="3c3frazph2i1p">
        AI features utilize OpenAI models to generate insights and summaries.
        Data is processed securely and is not used to train public models.
      </div>

      <div className="space-y-6" data-id="element-229" data-mp="3272vnwad9u4b">
        {[{
        title: 'Executive Summaries',
        desc: 'Auto-generate narrative summaries for completed assessments.',
        active: true
      }, {
        title: 'Blind Spot Detection',
        desc: 'Flag significant discrepancies between self and peer ratings.',
        active: true
      }, {
        title: 'Coaching Recommendations',
        desc: 'Suggest actionable coaching topics based on assessment results.',
        active: true
      }, {
        title: 'Sentiment Analysis',
        desc: 'Analyze open-text responses for overall sentiment trends.',
        active: false
      }].map((feature, i) => <div key={i} className="flex items-start justify-between border-b border-slate-100 pb-4 last:border-0" data-id="element-230" data-mp="66z30598615v" data-mpi={i}>
            <div data-id="element-231" data-mp="3tbyh63mqwusp">
              <div className="text-sm font-medium text-slate-900" data-id="element-232" data-mp="3tadnshc63hwf">
                {feature.title}
              </div>
              <div className="text-sm text-slate-500 mt-0.5" data-id="element-233" data-mp="39q5a1fl7wry3">
                {feature.desc}
              </div>
            </div>
            {feature.active ? <ToggleRight className="h-6 w-6 text-indigo-600 shrink-0 ml-4 cursor-pointer" data-id="element-234" data-mp="3jo2zu7iq6wst" /> : <ToggleLeft className="h-6 w-6 text-slate-300 shrink-0 ml-4 cursor-pointer" data-id="element-235" data-mp="3enonwee072pi" />}
          </div>)}
      </div>
    </div>;
}
function AuditLogsPanel() {
  return <div className="flex flex-col h-full" data-id="element-236" data-mp="3nod49fqvdf5k">
      <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50" data-id="element-237" data-mp="3nnbr95qamil7">
        <div data-id="element-238" data-mp="6azhtenqdj7k">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-239" data-mp="19tpvj1w9zz3">Audit Logs</h2>
          <p className="text-sm text-slate-500" data-id="element-240" data-mp="1bwpmusun7h0">
            System-wide activity and security events.
          </p>
        </div>
        <button className="border border-slate-200 text-slate-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors bg-white" data-id="element-241" data-mp="b89hdn4u3b94">
          Export CSV
        </button>
      </div>
      <div className="flex-1 overflow-auto" data-id="element-242" data-mp="irwnf2r5hvy0">
        <table className="w-full text-sm text-left" data-id="element-243" data-mp="ir4rtrxjzy7q">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-244" data-mp="wr5xdkorjttj">
            <tr data-id="element-245" data-mp="rrt8yvi8ueou">
              <th className="px-6 py-3 font-medium" data-id="element-246" data-mp="rte3il4k5xmp">Timestamp</th>
              <th className="px-6 py-3 font-medium" data-id="element-247" data-mp="42qjx2la5jkb">User</th>
              <th className="px-6 py-3 font-medium" data-id="element-248" data-mp="4308clqofinw">Action</th>
              <th className="px-6 py-3 font-medium" data-id="element-249" data-mp="1y8c2oxr2kv">Resource</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-250" data-mp="dstj5kizvtr3">
            {[{
            time: 'Oct 24, 14:32',
            user: 'Jane Doe',
            action: 'Generated Report',
            resource: 'Q3 Exec Summary'
          }, {
            time: 'Oct 24, 11:15',
            user: 'System',
            action: 'Sent Reminders',
            resource: 'Q3 Leadership Review (12)'
          }, {
            time: 'Oct 23, 09:45',
            user: 'Michael Scott',
            action: 'Created Assessment',
            resource: 'Sales Team Pulse'
          }, {
            time: 'Oct 22, 16:20',
            user: 'Jane Doe',
            action: 'Updated Settings',
            resource: 'AI Configuration'
          }, {
            time: 'Oct 22, 10:05',
            user: 'Sarah Connor',
            action: 'Imported Users',
            resource: 'Engineering Roster.csv'
          }].map((log, i) => <tr key={i} className="hover:bg-slate-50 text-slate-600" data-id="element-251" data-mp="26ombag9n8drj" data-mpi={i}>
                <td className="px-6 py-4 whitespace-nowrap text-slate-500" data-id="element-252" data-mp="qbffdyidgziq">
                  {log.time}
                </td>
                <td className="px-6 py-4 font-medium text-slate-900" data-id="element-253" data-mp="vba5w0y8jn39">
                  {log.user}
                </td>
                <td className="px-6 py-4" data-id="element-254" data-mp="b7me8oto6jeg">{log.action}</td>
                <td className="px-6 py-4" data-id="element-255" data-mp="760ucjipx4ur">{log.resource}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}